import { useCallback, useEffect, useMemo, useRef } from "react";
import { pluralize } from "@common/helpers/string";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { CommandsDirectory } from "@components/commands/lib";
import { useCommands } from "@components/commands/useCommands";
import {
    useArchiveInvoiceTemplate,
    useUnarchiveInvoiceTemplate,
} from "@api/invoiceTemplate.api";
import { InvoiceTemplateRow } from "./invoiceTemplates.types";

interface UseInvoiceTemplateBulkActionsParams {
    selectedRows: InvoiceTemplateRow[];
    clearSelection: () => void;
}

export function useInvoiceTemplateBulkActions({
    selectedRows,
    clearSelection,
}: UseInvoiceTemplateBulkActionsParams) {
    const {
        open: openCommands,
        close: closeCommands,
        registerOpenOverride,
        unregisterOpenOverride,
    } = useCommands();
    const { toast: successToast, error: errorToast } = useToaster();
    const { mutateAsync: archiveMutateAsync, isPending: isArchivePending } =
        useArchiveInvoiceTemplate();
    const { mutateAsync: unarchiveMutateAsync, isPending: isUnarchivePending } =
        useUnarchiveInvoiceTemplate();

    const archiveEligibleRows = useMemo(
        () => selectedRows.filter((row) => row.archivedAt == null),
        [selectedRows],
    );
    const unarchiveEligibleRows = useMemo(
        () => selectedRows.filter((row) => row.archivedAt != null),
        [selectedRows],
    );

    const runBulkAction = useCallback(
        async ({
            rows,
            runOne,
            verbPastTense,
            actionLabel,
        }: {
            rows: InvoiceTemplateRow[];
            runOne: (row: InvoiceTemplateRow) => Promise<void>;
            verbPastTense: string;
            actionLabel: string;
        }) => {
            if (rows.length === 0) {
                return;
            }
            closeCommands();
            const results = await Promise.allSettled(
                rows.map((row) => runOne(row)),
            );
            const succeededCount = results.filter(
                (result) => result.status === "fulfilled",
            ).length;
            const failedCount = results.length - succeededCount;

            if (succeededCount > 0) {
                successToast(
                    `${verbPastTense} ${succeededCount} ${pluralize(
                        "template",
                        succeededCount,
                    )}`,
                );
                clearSelection();
            }
            if (failedCount > 0) {
                errorToast(
                    `Failed to ${actionLabel} ${failedCount} ${pluralize(
                        "template",
                        failedCount,
                    )}`,
                );
            }
        },
        [closeCommands, successToast, errorToast, clearSelection],
    );

    const handleBulkArchive = useCallback(
        () =>
            void runBulkAction({
                rows: archiveEligibleRows,
                runOne: async (row) => {
                    await archiveMutateAsync({
                        entityId: row.entityId,
                        templateId: row.id,
                        suppressErrorToast: true,
                    });
                },
                verbPastTense: "Archived",
                actionLabel: "archive",
            }),
        [runBulkAction, archiveEligibleRows, archiveMutateAsync],
    );

    const handleBulkUnarchive = useCallback(
        () =>
            void runBulkAction({
                rows: unarchiveEligibleRows,
                runOne: async (row) => {
                    await unarchiveMutateAsync({
                        entityId: row.entityId,
                        templateId: row.id,
                        suppressErrorToast: true,
                    });
                },
                verbPastTense: "Unarchived",
                actionLabel: "unarchive",
            }),
        [runBulkAction, unarchiveEligibleRows, unarchiveMutateAsync],
    );

    const handleOpenBulkActions = useCallback(() => {
        if (selectedRows.length === 0) {
            return;
        }
        openCommands(CommandsDirectory.INVOICE_TEMPLATE_BULK, {
            selectedInvoiceTemplateCount: selectedRows.length,
            onInvoiceTemplateBulkArchive:
                archiveEligibleRows.length > 0 ? handleBulkArchive : undefined,
            onInvoiceTemplateBulkUnarchive:
                unarchiveEligibleRows.length > 0
                    ? handleBulkUnarchive
                    : undefined,
        });
    }, [
        openCommands,
        selectedRows.length,
        archiveEligibleRows.length,
        unarchiveEligibleRows.length,
        handleBulkArchive,
        handleBulkUnarchive,
    ]);

    const handleOpenBulkActionsRef = useRef(handleOpenBulkActions);
    handleOpenBulkActionsRef.current = handleOpenBulkActions;

    useEffect(() => {
        if (selectedRows.length === 0) {
            return;
        }
        registerOpenOverride(() => handleOpenBulkActionsRef.current());
        return () => unregisterOpenOverride();
    }, [selectedRows.length, registerOpenOverride, unregisterOpenOverride]);

    return {
        handleOpenBulkActions,
        isBulkActionPending: isArchivePending || isUnarchivePending,
    };
}
